import { useEffect } from "react";

export const SITE_URL = "https://primeprofit.live";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

const MANAGED = "data-seo-managed";

function setTag(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute(MANAGED, "true");
    document.head.appendChild(el);
  }
  apply(el);
}

function meta(attr: "name" | "property", key: string, content: string) {
  setTag(
    `meta[${attr}="${key}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute(attr, key);
      return m;
    },
    (el) => el.setAttribute("content", content),
  );
}

/** Client-side head manager: per-route title, description, canonical, OG/Twitter tags and JSON-LD. */
const Seo = ({ title, description, path, noindex, schema }: SeoProps) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    document.title = title;

    meta("name", "description", description);
    meta("property", "og:title", title);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:type", "website");
    meta("property", "og:site_name", "Prime Profit");
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:title", title);
    meta("name", "twitter:description", description);

    setTag(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      },
      (el) => el.setAttribute("href", url),
    );

    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      meta("name", "robots", "noindex, nofollow");
    } else if (robots?.getAttribute(MANAGED)) {
      robots.remove();
    }

    let ld: HTMLScriptElement | null = null;
    if (schema) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute(MANAGED, "route");
      ld.textContent = JSON.stringify(schema);
      document.head.appendChild(ld);
    }

    return () => {
      ld?.remove();
    };
  }, [title, description, path, noindex, schema]);

  return null;
};

export default Seo;
